import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { publicApi } from '../utils/api';
import type { LiveRaffleData, Participant, Winner } from '../types';
import PrizeWheel from '../components/PrizeWheel';

function LiveRafflePage() {
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const myParticipantId = searchParams.get('pid'); // Track if current user is a participant

  const [raffle, setRaffle] = useState<LiveRaffleData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [isSpinning, setIsSpinning] = useState(false);
  const [showWinnerReveal, setShowWinnerReveal] = useState(false);
  const [revealedWinner, setRevealedWinner] = useState<Participant | null>(null);
  const [showConfetti, setShowConfetti] = useState(false);

  // Use refs to maintain stable state during spin animation
  const lastSpinWinnerId = useRef<string | null>(null);
  const currentSpinWinner = useRef<Participant | null>(null);
  const spinningParticipants = useRef<Participant[]>([]);
  const hasStartedSpinning = useRef(false);

  const loadRaffle = useCallback(async () => {
    if (!id) return;
    try {
      const data = await publicApi.getLiveRaffle(id);

      // OPTIMIZED: If null, data hasn't changed (ETag match), skip processing
      if (data === null) {
        return;
      }

      setRaffle(data);
      setError('');

      // Calculate eligible participants (those who haven't won yet)
      const winnerIds = new Set(data.winners.map((w: Winner) => w.participant_id));
      const eligible = data.participants.filter((p: Participant) => !winnerIds.has(p.id));

      // Detect when a new spin starts
      // current_spin_winner is set when admin starts a spin, and cleared after confirming winner
      if (data.current_spin_winner && !hasStartedSpinning.current) {
        // New spin detected!
        currentSpinWinner.current = data.current_spin_winner;
        lastSpinWinnerId.current = data.current_spin_winner.id;
        // Freeze the participant list - include the spin winner since they're still "eligible"
        spinningParticipants.current = eligible;
        hasStartedSpinning.current = true;
        setIsSpinning(true);
      }

      // Detect when spin ends (current_spin_winner is cleared OR winner was added)
      if (!data.current_spin_winner && hasStartedSpinning.current && lastSpinWinnerId.current) {
        // Spin ended - find the winner
        const winnerParticipant = data.participants.find(p => p.id === lastSpinWinnerId.current);
        if (winnerParticipant) {
          setRevealedWinner(winnerParticipant);
          setShowWinnerReveal(true);

          // Check if current user is the winner - show confetti!
          if (myParticipantId && myParticipantId === lastSpinWinnerId.current) {
            setShowConfetti(true);
          }
        }

        // Reset spin state
        hasStartedSpinning.current = false;
        currentSpinWinner.current = null;
        spinningParticipants.current = [];
        lastSpinWinnerId.current = null;
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load raffle');
    } finally {
      setLoading(false);
    }
  }, [id, myParticipantId]);

  // OPTIMIZED: Adaptive polling - faster during spin, slower when idle
  useEffect(() => {
    loadRaffle();

    // Poll faster (500ms) during spin, slower (3s) when idle
    const getInterval = () => isSpinning ? 500 : 3000;

    let intervalId: NodeJS.Timeout;

    const scheduleNext = () => {
      intervalId = setTimeout(() => {
        loadRaffle();
        scheduleNext();
      }, getInterval());
    };

    scheduleNext();

    return () => clearTimeout(intervalId);
  }, [loadRaffle, isSpinning]);

  const handleSpinComplete = useCallback(() => {
    setIsSpinning(false);
    // Winner reveal will be triggered by the next poll when current_spin_winner is cleared
  }, []);

  if (loading) {
    return (
      <div className="page">
        <div className="loading">Loading raffle...</div>
      </div>
    );
  }

  if (!raffle) {
    return (
      <div className="page">
        <div className="error-message">Raffle not found</div>
        <Link to="/join" className="btn btn-secondary">Back</Link>
      </div>
    );
  }

  const winnerIds = new Set(raffle.winners.map((w: Winner) => w.participant_id));
  const eligibleParticipants = raffle.participants.filter((p: Participant) => !winnerIds.has(p.id));

  // Use frozen participants during spin, otherwise use current eligible list
  const wheelParticipants = isSpinning && spinningParticipants.current.length > 0
    ? spinningParticipants.current
    : eligibleParticipants.length > 0
      ? eligibleParticipants
      : raffle.participants;

  const isWaitingForSpin = raffle.status === 'active' && !isSpinning && !showWinnerReveal;
  const shouldShowWheel = isSpinning || (raffle.status === 'spinning' && wheelParticipants.length > 0);

  return (
    <div className="page">
      {/* Confetti overlay for winners */}
      {showConfetti && (
        <div className="confetti-container">
          {[...Array(100)].map((_, i) => (
            <div
              key={i}
              className="confetti"
              style={{
                left: `${Math.random() * 100}%`,
                animationDelay: `${Math.random() * 3}s`,
                backgroundColor: ['#1BBC9B', '#0490DA', '#FFC700', '#50CD89', '#F1416C', '#A091FE'][Math.floor(Math.random() * 6)],
              }}
            />
          ))}
        </div>
      )}

      <div className="page-header">
        <Link to="/join" className="back-link">&larr;</Link>
        <h1>{raffle.name}</h1>
        <span className={`status-badge status-${raffle.status}`}>{raffle.status}</span>
      </div>

      {error && <div className="error-message">{error}</div>}

      <div className="live-raffle-grid">
        <div>
          <div className="card">
            <h3>Participants ({raffle.participants.length})</h3>
            {raffle.participants.length === 0 ? (
              <p className="text-muted">No participants yet</p>
            ) : (
              <div className="participants-grid">
                {raffle.participants.map((p: Participant) => (
                  <div
                    key={p.id}
                    className={`participant-chip ${winnerIds.has(p.id) ? 'winner' : ''} ${p.id === myParticipantId ? 'is-me' : ''}`}
                  >
                    {p.first_name} {p.last_name}
                    {winnerIds.has(p.id) && ' 🏆'}
                    {p.id === myParticipantId && ' (You)'}
                  </div>
                ))}
              </div>
            )}
          </div>

          {raffle.winners.length > 0 && (
            <div className="card">
              <h3 className="accent-warning">Winners</h3>
              <div className="participants-grid">
                {raffle.winners.map((w: Winner) => (
                  <div key={w.id} className={`participant-chip winner ${w.participant_id === myParticipantId ? 'is-me' : ''}`}>
                    {w.first_name} {w.last_name}
                    {w.prize_name && ` - ${w.prize_name}`}
                    {w.participant_id === myParticipantId && ' (You!)'}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="wheel-container">
          {isWaitingForSpin && (
            <div className="card" style={{ textAlign: 'center' }}>
              <h3>Waiting for the drawing to begin...</h3>
              <p className="text-muted">
                The admin will spin the wheel when ready
              </p>
            </div>
          )}

          {showWinnerReveal && revealedWinner ? (
            <div className="winner-reveal-container">
              <div className={`winner-reveal ${showConfetti ? 'winner-is-me' : ''}`}>
                <div className="winner-reveal-icon">{showConfetti ? '🎊' : '🎉'}</div>
                <h2>{showConfetti ? 'YOU WON!' : 'We Have a Winner!'}</h2>
                <div className="winner-reveal-name">
                  {revealedWinner.first_name} {revealedWinner.last_name}
                </div>
                {showConfetti && (
                  <div className="winner-congrats">Congratulations!</div>
                )}
                <button
                  className="btn btn-primary"
                  onClick={() => {
                    setShowWinnerReveal(false);
                    setRevealedWinner(null);
                    setShowConfetti(false);
                  }}
                  style={{ marginTop: '1.5rem' }}
                >
                  Continue
                </button>
              </div>
            </div>
          ) : (
            <>
              {shouldShowWheel && (
                <PrizeWheel
                  participants={wheelParticipants}
                  spinning={isSpinning}
                  winner={currentSpinWinner.current}
                  onSpinComplete={handleSpinComplete}
                />
              )}
            </>
          )}

          {raffle.status === 'completed' && !showWinnerReveal && raffle.winners.length > 0 && (
            <div className="winner-announcement">
              <h2>Congratulations to Our Winner{raffle.winners.length > 1 ? 's' : ''}!</h2>
              {raffle.winners.map((w: Winner) => (
                <div key={w.id} className="winner-name">
                  {w.first_name} {w.last_name}
                  {w.prize_name && <span className="text-muted"> - {w.prize_name}</span>}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="live-raffle-footer">
        <p className="text-muted">
          {isSpinning
            ? 'The drawing is in progress! Good luck!'
            : raffle.status === 'completed'
            ? 'This raffle has ended. Thank you for participating!'
            : 'Stay on this page to watch the live drawing'}
        </p>
      </div>
    </div>
  );
}

export default LiveRafflePage;
